/**
 * @file 게시판 글 상세 — 시안(두 번째 이미지)의 구성을 그대로 따른다.
 *
 * <pre>
 *   [답글] [수정] [삭제] [이동 ▾] [목록]
 *   제목
 *   👤 작성자    작성일  조회 N
 *   ▾ 첨부 파일 총 N개(합계)   전체 다운로드
 *      📄 파일명  크기   미리보기
 *   본문
 *   💬 댓글 …
 *   ← 이전 글 / 다음 글 →
 * </pre>
 *
 * <h3>왜 별도 페이지인가(모달이 아니라)</h3>
 * <p>게시판 글은 첨부·본문·댓글이 길어 모달에 넣으면 스크롤이 두 겹(모달 안 + 페이지)이 된다.
 * 또 <b>주소로 공유</b>가 되어야 해서({@code /board/{typeId}/{postId}}) 라우트를 가진 화면으로 둔다.
 *
 * <h3>면 구분은 배경색이 아니라 테두리로</h3>
 * <p>첨부·댓글 영역을 회색({@code colors.bg})으로 칠하면 흰 카드 위에 회색 덩어리가 얹혀 화면이 탁해진다.
 * 디자인 시스템의 규칙(<i>elevation 대신 테두리로 면을 구분한다</i> — {@code presets/card.js})에 맞춰
 * 모두 흰 면 + 옅은 테두리로 구분하고, 회색은 <b>hover 같은 일시 상태</b>에만 쓴다.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    Avatar, Box, Collapse, IconButton, Menu, MenuItem, Paper, Stack, Tooltip, Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ActionButton from "../../../component/common/ActionButton";
import LoadingDots from "../../../component/common/LoadingDots";
import AttachmentPreviewModal from "../../mail/components/AttachmentPreviewModal";
import BoardCommentSection from "./BoardCommentSection";
import { boardApi } from "../api/boardApi";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { formatFileSize } from "../../../util/numberFormat";
import { C, fileTypeIcon, formatBoardDateTime, totalBytes } from "../boardShared";

/** 본문 HTML sanitize 설정 — 채널 피드와 같은 규칙(인라인 style + data:image 허용). */
const DOMPURIFY_CFG = {
    ADD_ATTR: ["style", "target"],
    ALLOWED_URI_REGEXP: /^(?:https?|ftp|blob|data:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp|svg\+xml))/i,
};

/** 서버 변환(오피스 포함) 미리보기 지원 형식 — 지원 안 되면 돋보기를 숨긴다. */
function previewable(a) {
    const m = (a.contentType || "").toLowerCase();
    const name = (a.originalFileName || "").toLowerCase();
    return m.includes("pdf") || m.startsWith("text/") || m.startsWith("image/")
        || m.includes("spreadsheet") || m.includes("ms-excel") || m.includes("csv")
        || m.includes("officedocument") || m.includes("msword") || m.includes("ms-powerpoint") || m.includes("opendocument")
        || /\.(pdf|xlsx|xls|xlsm|ods|csv|txt|log|md|doc|docx|ppt|pptx|odt|odp|rtf|png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
}

export default function BoardPostDetail({
    postId, boardTypes, myName, reloadKey, onBack, onOpenPost, onEdit, onReply, onChanged,
}) {
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [attachOpen, setAttachOpen] = useState(true);
    const [moveAnchor, setMoveAnchor] = useState(null);
    const [previewModal, setPreviewModal] = useState(null);

    const load = useCallback(async (countView) => {
        setLoading(true);
        try {
            const data = await boardApi.getPost(postId, countView);
            setPost(data);
        } catch {
            notify("글을 불러오지 못했습니다", "error");
            setPost(null);
        } finally {
            setLoading(false);
        }
    }, [postId]);

    // 진입할 때만 조회수를 올린다. 댓글 작성 후 갱신은 refresh() 가 countView=false 로 재조회.
    useEffect(() => { load(true); }, [load]);

    /**
     * 밖에서 글이 바뀌었을 때(수정 다이얼로그 저장 등) 다시 읽는다.
     *
     * <p>수정은 이 컴포넌트 밖의 다이얼로그가 처리하는데, 상세는 {@code key={postId}} 로 마운트를
     * 유지하므로 저장만으로는 화면이 옛 내용을 계속 보여준다. 첫 렌더에서는 위 effect 와 겹쳐
     * 두 번 조회하지 않도록 초기값(0)일 때는 건너뛴다.
     */
    useEffect(() => {
        if (!reloadKey) return;
        load(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadKey]);

    /**
     * 이 글이 바뀐 뒤의 갱신 경로 — <b>여기 하나로만 부른다</b>.
     *
     * <p>댓글·첨부가 바뀌면 목록의 {@code [N]} 배지·클립 아이콘도 달라지므로 부모에 알려야 한다.
     * 부모가 {@code reloadKey} 를 올리면 위 effect 가 상세를 다시 읽으므로, 여기서 {@code load} 를
     * 또 부르면 <b>같은 글을 두 번 조회</b>해 첨부 접힘·스크롤이 두 번 깜빡인다.
     * 부모가 없을 때(단독 사용)만 직접 읽는다.
     */
    const refresh = () => { if (onChanged) onChanged(); else load(false); };

    const handleDelete = async () => {
        if (!(await confirmDialog({
            message: "글을 삭제하시겠습니까?\n답글·댓글·첨부파일이 모두 함께 삭제되며 되돌릴 수 없습니다.",
        }))) return;
        return runApiAction({
            request: () => boardApi.deletePost(postId),
            successMsg: "글이 삭제되었습니다",
            errorMsg: serverMsgOr("글 삭제 실패"),
            onSuccess: () => { onChanged?.(); onBack(); },
        });
    };

    const handleMove = (targetTypeId) => {
        setMoveAnchor(null);
        return runApiAction({
            request: () => boardApi.movePost(postId, targetTypeId),
            successMsg: "글을 옮겼습니다",
            errorMsg: serverMsgOr("글 이동 실패"),
            onSuccess: (data) => { setPost(data); onChanged?.(); },
        });
    };

    const handleToggleNotice = () => runApiAction({
        request: () => boardApi.toggleNotice(postId),
        errorMsg: serverMsgOr("공지 설정 실패"),
        onSuccess: (data) => { setPost(data); onChanged?.(); },
    });

    const openPreview = (a) => {
        const isImage = (a.contentType || "").startsWith("image/");
        setPreviewModal(isImage
            // 이미지는 서버 변환을 타지 않고 URL 로 바로 띄운다.
            ? { attachment: { filename: a.originalFileName, mimeType: a.contentType, url: a.fileUrl } }
            : {
                attachment: { id: a.id, filename: a.originalFileName, mimeType: a.contentType },
                loader: () => boardApi.previewAttachment(a.id),
            });
    };

    if (loading && !post) return <LoadingDots />;
    if (!post) return null;

    const attachments = post.attachments || [];
    const cleanHtml = DOMPurify.sanitize(post.content || "", DOMPURIFY_CFG);

    return (
        <Box sx={{ p: 3, maxWidth: 1300, mx: "auto" }}>
            {/* 게시판명 — 어느 게시판의 글인지 항상 보이게(딥링크로 바로 들어오는 경우 대비). */}
            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.3px", color: C.textPrimary, mb: 2 }}>
                {post.boardTypeName}
            </Typography>

            {/* 액션 바 */}
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                <ActionButton variant="secondary" startIcon={false} onClick={() => onReply(post)}
                    sx={{ fontSize: 12.5 }} label="답글" />
                {post.editable && (
                    <ActionButton variant="edit" onClick={() => onEdit(post)} sx={{ fontSize: 12.5 }} />
                )}
                {post.deletable && (
                    <ActionButton variant="delete" onClick={handleDelete} sx={{ fontSize: 12.5 }} />
                )}
                {/* 이동·공지 고정은 전체 관리자만 — canManage 는 서버 판단값. */}
                {post.canManage && (
                    <>
                        <ActionButton variant="secondary" startIcon={false}
                            endIcon={<ArrowDropDownRoundedIcon sx={{ fontSize: 18 }} />}
                            onClick={e => setMoveAnchor(e.currentTarget)}
                            sx={{ fontSize: 12.5 }} label="이동" />
                        <Menu anchorEl={moveAnchor} open={Boolean(moveAnchor)} onClose={() => setMoveAnchor(null)}
                            slotProps={{ paper: { sx: { minWidth: 170 } } }}>
                            {boardTypes
                                .filter(t => t.id !== post.boardTypeId)
                                .map(t => (
                                    <MenuItem key={t.id} onClick={() => handleMove(t.id)} sx={{ fontSize: 13 }}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            {boardTypes.filter(t => t.id !== post.boardTypeId).length === 0 && (
                                <MenuItem disabled sx={{ fontSize: 13 }}>옮길 게시판이 없습니다</MenuItem>
                            )}
                        </Menu>
                        <Tooltip title={post.notice ? "공지 고정 해제" : "공지로 고정"}>
                            <IconButton size="small" onClick={handleToggleNotice}
                                sx={{ border: `1px solid ${post.notice ? C.primaryBorder : C.border}`,
                                    bgcolor: post.notice ? C.primaryBg : C.surface, borderRadius: 1.1 }}>
                                {post.notice
                                    ? <PushPinRoundedIcon sx={{ fontSize: 16, color: C.primary }} />
                                    : <PushPinOutlinedIcon sx={{ fontSize: 16, color: C.textMuted }} />}
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <ActionButton variant="secondary" startIcon={false} onClick={onBack}
                    sx={{ fontSize: 12.5 }} label="목록" />
            </Stack>

            <Paper elevation={0} sx={{
                border: `1px solid ${C.borderLight}`, borderRadius: 1.5, bgcolor: C.surface, overflow: "hidden",
            }}>
                {/* 제목 + 작성자 */}
                <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1} mb={2}>
                        {post.notice && (
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.primary, flexShrink: 0 }}>
                                [공지]
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 19, fontWeight: 800, color: C.textPrimary, lineHeight: 1.4 }}>
                            {post.title}
                        </Typography>
                    </Stack>

                    {post.parentId && (
                        <Typography
                            onClick={() => onOpenPost(post.parentId)}
                            sx={{
                                fontSize: 12.5, color: C.primary, mb: 1.5, cursor: "pointer", fontWeight: 600,
                                "&:hover": { textDecoration: "underline" },
                            }}>
                            ↳ 원글: {post.parentTitle}
                        </Typography>
                    )}

                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ width: 34, height: 34, fontSize: 13, bgcolor: C.primarySoft }}>
                            {post.authorName?.[0] ?? "?"}
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>
                                {post.authorName}
                                {post.authorPositionName && (
                                    <Box component="span" sx={{ fontWeight: 500, color: C.textMuted, ml: 0.75 }}>
                                        {post.authorPositionName}
                                        {post.authorDeptName ? ` · ${post.authorDeptName}` : ""}
                                    </Box>
                                )}
                            </Typography>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 0.2 }}>
                                <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>
                                    {formatBoardDateTime(post.createdAt)}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>
                                    조회 {post.viewCount ?? 0}
                                </Typography>
                                {post.updatedAt && (
                                    <Typography sx={{ fontSize: 12.5, color: "#cbd5e1" }}>
                                        수정 {formatBoardDateTime(post.updatedAt)}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                {/* 첨부 박스 — 총 개수·합계 용량 + 전체 다운로드. 접을 수 있다. */}
                {attachments.length > 0 && (
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton size="small" onClick={() => setAttachOpen(v => !v)} sx={{ p: 0.2 }}>
                                {attachOpen
                                    ? <ExpandLessRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />
                                    : <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />}
                            </IconButton>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textSecondary }}>
                                첨부 파일 총 {attachments.length}개
                                <Box component="span" sx={{ color: C.textMuted, fontWeight: 500 }}>
                                    ({formatFileSize(totalBytes(attachments))})
                                </Box>
                            </Typography>
                            {/*
                              전체 다운로드 — 서버 zip 묶음이 없어 파일별 downloadUrl 을 순차로 연다.
                              팝업 차단기가 연속 창을 막을 수 있어 <a> 클릭을 합성하는 방식을 쓴다.
                            */}
                            <Typography
                                onClick={() => downloadAll(attachments)}
                                sx={{
                                    fontSize: 13, fontWeight: 600, color: C.primary, cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                }}>
                                전체 다운로드
                            </Typography>
                        </Stack>

                        <Collapse in={attachOpen}>
                            {/* 흰 면 + 테두리로 묶는다 — 회색으로 칠하면 카드 위에 얹힌 덩어리처럼 보인다. */}
                            <Box sx={{ mt: 1, border: `1px solid ${C.border}`, borderRadius: 1.5, bgcolor: C.surface, p: 1 }}>
                                {attachments.map(a => (
                                    <Stack key={a.id} direction="row" alignItems="center" spacing={1}
                                        sx={{ px: 1, py: 0.7, borderRadius: 1, "&:hover": { bgcolor: C.bg } }}>
                                        {fileTypeIcon(a)}
                                        <Typography
                                            component="a"
                                            href={a.downloadUrl || a.fileUrl}
                                            sx={{
                                                fontSize: 13, fontWeight: 600, color: C.textPrimary,
                                                textDecoration: "none", minWidth: 0,
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                "&:hover": { color: C.primary, textDecoration: "underline" },
                                            }}>
                                            {a.originalFileName}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }}>
                                            {formatFileSize(a.fileSize)}
                                        </Typography>
                                        {previewable(a) && (
                                            <Stack direction="row" alignItems="center" spacing={0.2}
                                                onClick={() => openPreview(a)}
                                                sx={{
                                                    cursor: "pointer", flexShrink: 0, color: C.primary,
                                                    "&:hover": { textDecoration: "underline" },
                                                }}>
                                                <ZoomInRoundedIcon sx={{ fontSize: 15 }} />
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "inherit" }}>
                                                    미리보기
                                                </Typography>
                                            </Stack>
                                        )}
                                        {post.editable && (
                                            <Tooltip title="첨부 삭제">
                                                <IconButton size="small" sx={{ p: 0.3, flexShrink: 0 }}
                                                    onClick={async () => {
                                                        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
                                                        return runApiAction({
                                                            request: () => boardApi.deleteAttachment(a.id),
                                                            errorMsg: serverMsgOr("첨부 삭제 실패"),
                                                            onSuccess: refresh,
                                                        });
                                                    }}>
                                                    <DeleteRoundedIcon sx={{ fontSize: 15, color: C.error }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                )}

                {/* 본문 */}
                <Box
                    className="board-content"
                    sx={{
                        px: 3, pb: 3,
                        fontSize: 14, lineHeight: 1.85, color: "#1e293b",
                        wordBreak: "break-word",
                        "& img": { maxWidth: "100%", height: "auto", borderRadius: "6px", display: "block", my: 1 },
                        "& table": { borderCollapse: "collapse", maxWidth: "100%" },
                        "& td, & th": { border: `1px solid ${C.border}`, padding: "6px 10px" },
                        "& a": { color: C.primary },
                        "& p": { margin: "0 0 0.6em" },
                    }}
                    dangerouslySetInnerHTML={{ __html: cleanHtml }}
                />

                {/* 답글 목록 — 원글에 달린 답글로 바로 이동. */}
                {post.replies?.length > 0 && (
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, mb: 0.75 }}>
                            답글 {post.replies.length}
                        </Typography>
                        {post.replies.map(r => (
                            <Stack key={r.id} direction="row" alignItems="center" spacing={1}
                                onClick={() => onOpenPost(r.id)}
                                sx={{
                                    px: 1.5, py: 0.9, borderRadius: 1, cursor: "pointer",
                                    border: `1px solid ${C.border}`, mb: 0.5,
                                    "&:hover": { bgcolor: C.primaryBg, borderColor: C.primaryBorder },
                                }}>
                                <Typography noWrap sx={{ flex: 1, fontSize: 13, color: "#334155" }}>
                                    └ {r.title}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }}>
                                    {r.authorName}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                )}

                {/* 댓글 — 본문과는 옅은 가로선 하나로만 나눈다(면을 회색으로 칠하지 않는다). */}
                <Box sx={{ borderTop: `1px solid ${C.borderLight}`, pt: 2 }}>
                    <BoardCommentSection
                        postId={postId}
                        comments={post.comments || []}
                        myName={myName}
                        onRefresh={refresh}
                    />
                </Box>
            </Paper>

            {/* 이전 / 다음 글 */}
            <Paper elevation={0} sx={{
                mt: 2, border: `1px solid ${C.borderLight}`, borderRadius: 1.5,
                bgcolor: C.surface, overflow: "hidden",
            }}>
                <NavRow label="이전 글" icon={<KeyboardArrowUpRoundedIcon sx={{ fontSize: 17 }} />}
                    link={post.prev} onOpen={onOpenPost} />
                <NavRow label="다음 글" icon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 17 }} />}
                    link={post.next} onOpen={onOpenPost} />
            </Paper>

            {previewModal && (
                <AttachmentPreviewModal
                    open
                    attachment={previewModal.attachment}
                    loader={previewModal.loader}
                    onClose={() => setPreviewModal(null)}
                />
            )}
        </Box>
    );
}

/** 이전/다음 글 한 줄. 링크가 없으면 클릭 불가 상태로 그대로 남긴다(줄이 사라지면 위치가 흔들린다). */
function NavRow({ label, icon, link, onOpen }) {
    const enabled = Boolean(link);
    return (
        <Stack direction="row" alignItems="center" spacing={1}
            onClick={enabled ? () => onOpen(link.id) : undefined}
            sx={{
                px: 2, py: 1.15,
                cursor: enabled ? "pointer" : "default",
                "&:hover": enabled ? { bgcolor: C.bg } : {},
            }}>
            <Box sx={{ display: "flex", color: C.textMuted, flexShrink: 0 }}>{icon}</Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.textMuted, width: 52, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography noWrap sx={{
                flex: 1, minWidth: 0, fontSize: 13,
                color: enabled ? "#334155" : "#cbd5e1",
            }}>
                {enabled ? link.title : "없습니다"}
            </Typography>
        </Stack>
    );
}

/**
 * 첨부 전체 다운로드.
 *
 * <p>{@code window.open} 을 연속 호출하면 두 번째부터 팝업 차단에 걸린다. 대신 숨은 {@code <a download>}
 * 를 만들어 클릭을 합성하고, 브라우저가 동시 요청을 버리지 않도록 약간의 간격을 둔다.
 * 파일이 많으면 브라우저의 다운로드 목록에 순차로 쌓인다.
 */
function downloadAll(attachments) {
    attachments.forEach((a, i) => {
        setTimeout(() => {
            const el = document.createElement("a");
            el.href = a.downloadUrl || a.fileUrl;
            el.rel = "noreferrer";
            document.body.appendChild(el);
            el.click();
            el.remove();
        }, i * 350);
    });
}
